import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Save, Percent, Banknote } from 'lucide-react'
import { toast } from 'sonner'
import { systemService, type CommissionSettingsPayload } from '@/services/systemService'
import { getFriendlyError } from '@/utils/getFriendlyError'

const defaults: CommissionSettingsPayload = {
  bdrMeetingCommissionReais: 30,
  closerRecorrenteFirstParcelPercent: 80,
  closerProjetoPercent: 10,
  commissionCalculationPeriod: 'mensal',
  commissionPaymentDay: 5,
  commissionClosingGraceDays: 10,
  commissionMinimumPaymentReais: 100,
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
  suffix,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  prefix?: string
  suffix?: string
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
        {label}
      </label>
      <div className='flex items-center gap-2'>
        {prefix && <span className='text-[var(--text-muted)] text-[13px]'>{prefix}</span>}
        <input
          type='number'
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={0}
          className='w-28 bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)] transition-colors min-h-[44px]'
        />
        {suffix && <span className='text-[var(--text-muted)] text-[13px]'>{suffix}</span>}
      </div>
    </div>
  )
}

export function FinanceSettingsTab() {
  const [settings, setSettings] = useState<CommissionSettingsPayload>(defaults)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    systemService
      .getAdminSettings()
      .then((data) => {
        if (!active) return
        setSettings({
          bdrMeetingCommissionReais: Number(data.bdrMeetingCommissionReais ?? defaults.bdrMeetingCommissionReais),
          closerRecorrenteFirstParcelPercent: Number(
            data.closerRecorrenteFirstParcelPercent ?? defaults.closerRecorrenteFirstParcelPercent,
          ),
          closerProjetoPercent: Number(data.closerProjetoPercent ?? defaults.closerProjetoPercent),
          commissionCalculationPeriod:
            data.commissionCalculationPeriod === 'semanal' ? 'semanal' : 'mensal',
          commissionPaymentDay: Number(data.commissionPaymentDay ?? defaults.commissionPaymentDay),
          commissionClosingGraceDays: Number(
            data.commissionClosingGraceDays ?? defaults.commissionClosingGraceDays,
          ),
          commissionMinimumPaymentReais: Number(
            data.commissionMinimumPaymentReais ?? defaults.commissionMinimumPaymentReais,
          ),
        })
      })
      .catch((err) => {
        toast.error(getFriendlyError(err, 'Não foi possível carregar as configurações financeiras'))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const update = (partial: Partial<CommissionSettingsPayload>) => {
    setSettings((prev) => ({ ...prev, ...partial }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await systemService.updateSettings(settings)
      toast.success('Configurações financeiras salvas')
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível salvar'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='glass-card space-y-6'>
      <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
        Financeiro
      </h3>
      <p className='text-[13px] text-[var(--text-muted)]'>
        BDR recebe valor fixo por reunião realizada. Closer recebe percentual da primeira parcela
        no recorrente, ou do valor do projeto. A comissão de parceiro continua na regra da pirâmide.
      </p>

      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <Percent size={16} className='text-[var(--brand-primary)]' />
          <h4 className='font-semibold text-[var(--text-primary)] text-[14px]'>Comissões</h4>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pl-6'>
          <NumberField
            label='BDR por reunião'
            value={settings.bdrMeetingCommissionReais}
            onChange={(v) => update({ bdrMeetingCommissionReais: v })}
            prefix='R$'
          />
          <NumberField
            label='Closer — 1ª parcela'
            value={settings.closerRecorrenteFirstParcelPercent}
            onChange={(v) => update({ closerRecorrenteFirstParcelPercent: v })}
            suffix='%'
          />
          <NumberField
            label='Closer — projeto'
            value={settings.closerProjetoPercent}
            onChange={(v) => update({ closerProjetoPercent: v })}
            suffix='%'
          />
          <div className='flex flex-col gap-1.5'>
            <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
              Período de cálculo
            </label>
            <select
              value={settings.commissionCalculationPeriod}
              onChange={(e) =>
                update({ commissionCalculationPeriod: e.target.value as 'semanal' | 'mensal' })
              }
              className='bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-[13px] rounded-md px-3 py-2 outline-none min-h-[44px]'
            >
              <option value='semanal'>Semanal</option>
              <option value='mensal'>Mensal</option>
            </select>
          </div>
        </div>
      </div>

      <div className='border-t border-[var(--glass-border)]' />

      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <Banknote size={16} className='text-[var(--brand-primary)]' />
          <h4 className='font-semibold text-[var(--text-primary)] text-[14px]'>Pagamentos</h4>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 pl-6'>
          <NumberField
            label='Dia do pagamento'
            value={settings.commissionPaymentDay}
            onChange={(v) => update({ commissionPaymentDay: v })}
            suffix='do mês'
          />
          <NumberField
            label='Carência de fechamento'
            value={settings.commissionClosingGraceDays}
            onChange={(v) => update({ commissionClosingGraceDays: v })}
            suffix='dias'
          />
          <NumberField
            label='Mínimo para pagamento'
            value={settings.commissionMinimumPaymentReais}
            onChange={(v) => update({ commissionMinimumPaymentReais: v })}
            prefix='R$'
          />
        </div>
      </div>

      <div className='pt-2'>
        <Button
          variant='primary'
          size='sm'
          icon={<Save size={13} />}
          loading={saving || loading}
          onClick={handleSave}
        >
          Salvar
        </Button>
      </div>
    </div>
  )
}
